/**
 * Performance — Wave/Pack-Performance mit Mitarbeiter-Analyse und Effizienz-Tab.
 *
 * Features:
 * - Wave- und Pack-Statistiken (Dauer, Picks, Anzahl)
 * - Mitarbeiter-Ranking nach Picks/Packs pro Stunde
 * - Effektive Arbeitszeit-Analyse, Idle-Time-Auswertung
 * - Datumsfilter, Warehouse-Filter, Shared-Account-Erkennung
 *
 * @page /performance
 * @roles admin, manager
 * @api /api/performance/all, /api/performance/pack-all, /api/performance/warehouses
 * @api /api/performance/data-range, /api/performance/effektive-arbeitszeit
 * @api /api/performance/mitarbeiter/:id, /api/reports/idle-time/summary
 * @i18n performance
 */
import { useEffect, useMemo, useState, useCallback, Fragment, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { getDateRange } from '../utils/dateUtils';
import DateRangeFilter from '../components/DateRangeFilter';
import { PageHeader } from '../components/ui/PageHeader';
import { KpiCard } from '../components/ui/KpiCard';
import { Badge } from '../components/ui/Badge';
import { useLanguage } from '../context/LanguageContext';
import EffizienzTab from './EffizienzTab';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { Weight, Clock, Settings, Timer, Package, Gauge } from 'lucide-react';
import ModulEinstellungen from '../components/ModulEinstellungen';

/* ─── Helpers ──────────────────────────────────────────── */
const API = import.meta.env.VITE_API_URL || '';

function fmt(n) {
  if (n == null || n === 0) return '0';
  return Number(n).toLocaleString('de-DE');
}

/** Format minutes as X,Xmin (one decimal, German comma) */
function formatMinutes(m) {
  if (m == null || isNaN(m)) return '–';
  return m.toFixed(1).replace('.', ',') + 'min';
}

/** Average minutes per pick/pack */
function avgMin(duration, count) {
  if (!count || !duration) return null;
  return duration / count;
}

/** Color based on avg minutes per pick/pack */
function avgColor(duration, count) {
  const mins = avgMin(duration, count);
  if (mins == null) return 'var(--text-tertiary)';
  if (mins < 10) return '#34d399';
  if (mins <= 20) return '#fbbf24';
  return '#ef4444';
}

function parseDate(s) { return s ? String(s).slice(0, 10) : null; }

const PERF = '#34d399';
const BLUE = '#338edc';
const PURPLE = '#66ABE5';
const WARN = '#fbbf24';
const CYAN = '#22d3ee';

/** Detect wave type from name */
function waveType(name) {
  if (!name) return 'normal';
  const n = name.toLowerCase();
  if (/dringend/i.test(n)) return 'dringend';
  if (/sop/i.test(n)) return 'sop';
  return 'normal';
}

/** Known shared/system accounts */
const SHARED_ACCOUNTS = new Set([
  'outbound_de', 'outbound_at', 'outbound_bg',
  'inbound_de', 'inbound_at', 'inbound_bg',
  'lager_de', 'lager_at', 'lager_bg',
]);

function isSharedAccount(empName) {
  if (!empName) return false;
  return SHARED_ACCOUNTS.has(empName.toLowerCase().trim()) || /^(outbound|inbound|lager)_/i.test(empName);
}

/* recompute helpers removed — all aggregation is now server-side */

/* ─── KPI Card: uses DS KpiCard from ../components/ui/KpiCard ─── */

/* ─── TrendChart (pure SVG) ───────────────────────────── */
function TrendChart({ data, dataKey, label, color = PERF, t }) {
  if (!data || data.length < 2) return <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">{t('performance.notEnoughData')}</div>;

  const W = 600, H = 120;
  const values = data.map(d => d[dataKey] || 0);
  const max = Math.max(...values, 1);
  const pad = { t: 10, r: 10, b: 24, l: 10 };
  const cw = W - pad.l - pad.r;
  const ch = H - pad.t - pad.b;

  const pts = values.map((v, i) => {
    const x = pad.l + (i / (values.length - 1)) * cw;
    const y = pad.t + ch - (v / max) * ch;
    return `${x},${y}`;
  }).join(' ');

  const area = `${pad.l},${pad.t + ch} ${pts} ${pad.l + cw},${pad.t + ch}`;

  return (
    <div>
      <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-1">{label}</div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} className="overflow-visible">
        <polyline points={area} fill={color} fillOpacity={0.08} stroke="none" />
        <polyline points={pts} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {data.map((d, i) => {
          const x = pad.l + (i / (values.length - 1)) * cw;
          if (data.length > 14 && i % Math.ceil(data.length / 7) !== 0 && i !== data.length - 1) return null;
          const dateStr = d.datum ? String(d.datum).slice(0, 10) : null;
          const ds = dateStr ? new Date(dateStr + 'T12:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '';
          return <text key={i} x={x} y={H - 4} fill="var(--text-muted)" fontSize={9} textAnchor="middle">{ds}</text>;
        })}
        {values.map((v, i) => {
          const x = pad.l + (i / (values.length - 1)) * cw;
          const y = pad.t + ch - (v / max) * ch;
          return <circle key={i} cx={x} cy={y} r={2.5} fill={color} opacity={0.7} />;
        })}
      </svg>
    </div>
  );
}

/* ─── MA Detail Panel ─────────────────────────────────── */
function MaDetail({ maId, von, bis, onClose, t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/performance/mitarbeiter/${maId}?von=${von}&bis=${bis}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [maId, von, bis]);

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 w-[480px] bg-surface-elevated border-l border-line-hover shadow-2xl z-50 overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-content">{t('performance.wavePerformance')}</h3>
            <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-content text-xl">&times;</button>
          </div>
          {loading && <div className="py-8 text-center text-sm text-[var(--text-secondary)]">{t('performance.loading')}</div>}
          {!loading && !data?.summary && <div className="py-8 text-center text-sm text-[var(--text-tertiary)]">{t('performance.noData')}</div>}
          {!loading && data?.summary && (
            <>
              <div className="grid grid-cols-3 gap-3 mb-5">
                {[
                  { l: t('performance.waves'), v: data.summary.waves, c: BLUE },
                  { l: t('performance.articles'), v: fmt(data.summary.articles), c: PERF },
                  { l: t('performance.artPerH'), v: data.summary.articles_per_hour, c: PURPLE },
                ].map(k => (
                  <div key={k.l} className="bg-surface-card border border-line rounded-card p-3 text-center">
                    <div className="text-lg font-mono font-bold text-content">{k.v}</div>
                    <div className="text-[10px] text-[var(--text-tertiary)]">{k.l}</div>
                  </div>
                ))}
              </div>
              {data.daily?.length >= 2 && (
                <div className="mb-5 bg-surface-card border border-line rounded-card p-3">
                  <TrendChart data={data.daily} dataKey="articles" label={t('performance.artPerDay')} color={PERF} t={t} />
                </div>
              )}
              {data.merchants?.length > 0 && (
                <div className="mb-5">
                  <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-2">{t('performance.topCustomers')}</div>
                  {data.merchants.map((m, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-surface-card rounded-card text-xs mb-1">
                      <span className="text-content-secondary truncate mr-2">{m.name}</span>
                      <span className="font-mono font-bold text-content shrink-0">{fmt(m.articles)} {t('performance.art')}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* ─── MultiSelect Component ──────────────────────────── */
function MultiSelect({ options, selected, onChange, placeholder }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = (options || []).filter(o =>
    o.name && !selected.some(s => s.name === o.name) &&
    o.name.toLowerCase().includes(text.toLowerCase())
  );

  return (
    <div ref={ref} className="relative">
      <div className="flex flex-wrap items-center gap-1 bg-surface-inset border border-line-hover rounded-lg px-2 py-1 min-w-[160px] max-w-[280px] cursor-text"
        onClick={() => setOpen(true)}>
        {selected.map(s => (
          <span key={s.name} className="inline-flex items-center gap-1 bg-primary-500/15 text-primary-500 text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap">
            {s.name}
            <button onClick={(e) => { e.stopPropagation(); onChange(selected.filter(x => x.name !== s.name)); }}
              className="hover:text-white leading-none">&times;</button>
          </span>
        ))}
        <input type="text" value={text} onChange={e => setText(e.target.value)} onFocus={() => setOpen(true)}
          placeholder={selected.length === 0 ? placeholder : ''}
          className="bg-transparent border-none outline-none text-xs text-content placeholder-content-dim flex-1 min-w-[60px] py-0.5" />
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-surface-elevated border border-line-hover rounded-card shadow-sm">
          {filtered.slice(0, 50).map(o => (
            <button key={o.name} onClick={() => { onChange([...selected, o]); setText(''); }}
              className="w-full text-left px-3 py-1.5 text-xs text-content hover:bg-surface-hover transition-colors">
              {o.name}
            </button>
          ))}
          {filtered.length > 50 && <div className="px-3 py-1.5 text-[10px] text-[var(--text-tertiary)]">{t('performance.more', { n: filtered.length - 50 })}</div>}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════ */
export default function Performance() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const urlKunde = searchParams.get('kunde') || '';

  // ── State ──
  const [von, setVon] = useState('');
  const [bis, setBis] = useState('');
  const [preset, setPreset] = useState('');
  const [warehouse, setWarehouse] = useState('');
  const [warehouses, setWarehouses] = useState([]);
  const [dataRange, setDataRange] = useState(null);
  const [data, setData] = useState(null);
  const [packData, setPackData] = useState(null);
  const [durchlaufzeitData, setDurchlaufzeitData] = useState(null);
  const [durchlaufzeitTrend, setDurchlaufzeitTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('pick');
  const [activeTab, setActiveTab] = useState('waves');
  const [selectedMa, setSelectedMa] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [sortCol, setSortCol] = useState('start_time');
  const [sortDir, setSortDir] = useState('desc');
  const [searchMa, setSearchMa] = useState('');

  // ── Filter state ──
  const [selEmployees, setSelEmployees] = useState([]);
  const [selMerchants, setSelMerchants] = useState([]);
  const [kundeApplied, setKundeApplied] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filterMaxDuration, setFilterMaxDuration] = useState(60);
  const [filterCrossDayWaves, setFilterCrossDayWaves] = useState(true);
  const [filterMaxDurEnabled, setFilterMaxDurEnabled] = useState(false);
  const [filterWaveTypes, setFilterWaveTypes] = useState([]);
  const [filterWaveNameContains, setFilterWaveNameContains] = useState('');
  const [filterWaveNameEnabled, setFilterWaveNameEnabled] = useState(false);
  const [sopWaveFilter, setSopWaveFilter] = useState('alle'); // 'alle' | 'sop' | 'normal' | 'dringend'
  const [lbSopFilter, setLbSopFilter] = useState('alle'); // 'alle' | 'normal' | 'sop' (leaderboard)
  const [showSharedAccounts, setShowSharedAccounts] = useState(false);
  const [effektivData, setEffektivData] = useState(null);

  // ── Init: load date range, then set smart defaults ──
  useEffect(() => {
    let active = true;
    fetch(`${API}/api/performance/warehouses`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : []).then(d => { if (active) setWarehouses(d); }).catch(() => {});

    fetch(`${API}/api/performance/data-range`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(dr => {
        if (!active || !dr) return;
        const last = parseDate(dr.last_date);
        const first = parseDate(dr.first_date);
        setDataRange({ first_date: first, last_date: last, total: dr.total });

        if (last) {
          const range = getDateRange('monat');
          setVon(range.von);
          setBis(range.bis);
          setPreset('monat');
        } else {
          const range = getDateRange('monat');
          setVon(range.von);
          setBis(range.bis);
          setPreset('monat');
        }
      })
      .catch(() => {
        const range = getDateRange('monat');
        setVon(range.von);
        setBis(range.bis);
        setPreset('monat');
      });

    return () => { active = false; };
  }, []);

  // ── Debounced wave name filter ──
  const [debouncedWaveName, setDebouncedWaveName] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedWaveName(filterWaveNameEnabled ? filterWaveNameContains : ''), 300);
    return () => clearTimeout(t);
  }, [filterWaveNameContains, filterWaveNameEnabled]);

  // ── Build query string from all filter state ──
  const buildQueryString = useCallback(() => {
    const p = new URLSearchParams();
    if (von) p.set('von', von);
    if (bis) p.set('bis', bis);
    if (warehouse) p.set('warehouse', warehouse);
    if (selMerchants.length > 0) p.set('merchant', selMerchants.map(m => m.name).join(','));
    if (selEmployees.length > 0) p.set('employee', selEmployees.map(e => e.id).join(','));
    if (filterMaxDurEnabled && filterMaxDuration > 0) p.set('maxDuration', filterMaxDuration);
    p.set('crossDay', filterCrossDayWaves ? 'true' : 'false');
    if (sopWaveFilter !== 'alle') p.set('sopFilter', sopWaveFilter);
    if (filterWaveTypes.length > 0) p.set('waveTypes', filterWaveTypes.join(','));
    if (debouncedWaveName.trim()) p.set('waveNameContains', debouncedWaveName.trim());
    if (lbSopFilter !== 'alle') p.set('lbSopFilter', lbSopFilter);
    return p.toString();
  }, [von, bis, warehouse, selMerchants, selEmployees, filterMaxDurEnabled, filterMaxDuration, filterCrossDayWaves, sopWaveFilter, filterWaveTypes, debouncedWaveName, lbSopFilter]);

  // ── Load data when any filter changes ──
  useEffect(() => {
    if (!von || !bis) return;
    setLoading(true);
    setError(null);
    const qs = buildQueryString();
    const controller = new AbortController();

    const fetchWave = fetch(`${API}/api/performance/all?${qs}`, { credentials: 'include', signal: controller.signal })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); });
    const fetchPack = fetch(`${API}/api/performance/pack-all?${qs}`, { credentials: 'include', signal: controller.signal })
      .then(r => r.ok ? r.json() : null).catch(e => { if (e.name !== 'AbortError') return null; throw e; });
    const fetchEffektiv = fetch(`${API}/api/performance/effektive-arbeitszeit?von=${von}&bis=${bis}`, { credentials: 'include', signal: controller.signal })
      .then(r => r.ok ? r.json() : null).catch(e => { if (e.name !== 'AbortError') return null; throw e; });
    const fetchDLZ = fetch(`${API}/api/performance/durchlaufzeit?days=30`, { credentials: 'include', signal: controller.signal })
      .then(r => r.ok ? r.json() : null).catch(e => { if (e.name !== 'AbortError') return null; throw e; });
    const fetchDLZTrend = fetch(`${API}/api/performance/durchlaufzeit/trend?days=30`, { credentials: 'include', signal: controller.signal })
      .then(r => r.ok ? r.json() : null).catch(e => { if (e.name !== 'AbortError') return null; throw e; });

    Promise.all([fetchWave, fetchPack, fetchEffektiv, fetchDLZ, fetchDLZTrend])
      .then(([waveD, packD, effD, dlzD, dlzTrendD]) => {
        setData(waveD); setPackData(packD); setEffektivData(effD);
        if (dlzD) setDurchlaufzeitData(dlzD);
        if (Array.isArray(dlzTrendD)) setDurchlaufzeitTrend(dlzTrendD);
        setLoading(false);
      })
      .catch(e => { if (e.name === 'AbortError') return; setError(e.message); setData(null); setPackData(null); setEffektivData(null); setLoading(false); });

    return () => controller.abort();
  }, [buildQueryString]);

  // ── Range change handler ──
  const handleRangeChange = useCallback(({ von: v, bis: b, preset: p }) => {
    setVon(v); setBis(b); setPreset(p);
  }, []);

  const jumpToLastData = useCallback(() => {
    if (!dataRange?.first_date || !dataRange?.last_date) return;
    setVon(dataRange.first_date);
    setBis(dataRange.last_date);
    setPreset('alles');
  }, [dataRange]);

  // ── Data from backend (server-side aggregated) ──
  const summary = data?.summary || {};
  const packSummary = packData?.summary || {};
  const leaderboard = data?.leaderboard || [];
  const packLeaderboard = packData?.leaderboard || [];
  const daily = data?.daily || [];
  const packDaily = packData?.daily || [];
  const sopCounts = data?.sopCounts || { sop: 0, normal: 0, dringend: 0, total: 0 };
  const waves = data?.waves || [];
  const packs = packData?.packs || [];
  const waveStats = { mean: data?.durationStats?.mean || 0, sd: data?.durationStats?.stddev || Infinity };
  const packStats = { mean: packData?.durationStats?.mean || 0, sd: packData?.durationStats?.stddev || Infinity };
  const totalWaveCount = data?.totalCount || 0;
  const totalPackCount = packData?.totalCount || 0;

  // ── Dropdown options from backend ──
  const allEmployees = useMemo(() => {
    const map = new Map();
    (data?.allEmployees || []).forEach(e => { if (e.name) map.set(e.name, e); });
    (packData?.allEmployees || []).forEach(e => { if (e.name) map.set(e.name, e); });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [data, packData]);

  const allMerchants = useMemo(() => {
    const set = new Set([...(data?.allMerchants || []), ...(packData?.allMerchants || [])]);
    return [...set].sort().map(name => ({ name }));
  }, [data, packData]);

  // Auto-apply ?kunde= URL param to merchant filter
  useEffect(() => {
    if (kundeApplied || !urlKunde || allMerchants.length === 0) return;
    const q = urlKunde.toLowerCase();
    const match = allMerchants.find(m => m.name.toLowerCase().includes(q));
    if (match) {
      setSelMerchants([match]);
      setShowFilterPanel(true);
    }
    setKundeApplied(true);
  }, [urlKunde, allMerchants, kundeApplied]);

  const waveTypes = data?.waveTypes || [];

  // ── Leaderboard search + shared account filter (client-side on small dataset) ──
  const filteredLeaderboard = useMemo(() => {
    let lb = mode === 'pack' ? packLeaderboard : leaderboard;
    const individual = lb.filter(m => !m.shared);
    let result = showSharedAccounts ? lb : individual;
    if (searchMa) {
      const q = searchMa.toLowerCase();
      result = result.filter(m => m.name?.toLowerCase().includes(q));
    }
    return result.map((m, i) => ({ ...m, rank: i + 1 }));
  }, [leaderboard, packLeaderboard, searchMa, mode, showSharedAccounts]);

  const sharedAccountCount = useMemo(() => {
    const lb = mode === 'pack' ? packLeaderboard : leaderboard;
    return lb.filter(m => m.shared).length;
  }, [leaderboard, packLeaderboard, mode]);

  // Combined leaderboard for "both" mode
  const combinedLeaderboard = useMemo(() => {
    if (mode !== 'both') return [];
    const map = {};
    for (const m of leaderboard) {
      map[m.mitarbeiter_id] = {
        ...m,
        pick_articles: m.articles,
        pick_aph: m.articles_per_hour,
        pick_waves: m.waves,
        pack_articles: 0,
        pack_aph: 0,
        pack_packs: 0,
      };
    }
    for (const m of packLeaderboard) {
      if (map[m.mitarbeiter_id]) {
        map[m.mitarbeiter_id].pack_articles = m.articles;
        map[m.mitarbeiter_id].pack_aph = m.articles_per_hour;
        map[m.mitarbeiter_id].pack_packs = m.packs;
      } else {
        map[m.mitarbeiter_id] = {
          ...m,
          pick_articles: 0,
          pick_aph: 0,
          pick_waves: 0,
          pack_articles: m.articles,
          pack_aph: m.articles_per_hour,
          pack_packs: m.packs,
        };
      }
    }
    let arr = Object.values(map)
      .map(m => ({ ...m, total_articles: (m.pick_articles || 0) + (m.pack_articles || 0) }))
      .sort((a, b) => b.total_articles - a.total_articles);
    if (searchMa) {
      const q = searchMa.toLowerCase();
      arr = arr.filter(m => m.name?.toLowerCase().includes(q));
    }
    return arr.map((m, i) => ({ ...m, rank: i + 1 }));
  }, [leaderboard, packLeaderboard, mode, searchMa]);

  // Effektive Arbeitszeit lookup map
  const effektivMap = useMemo(() => {
    if (!effektivData?.mitarbeiter) return {};
    const m = {};
    effektivData.mitarbeiter.forEach(e => { m[e.mitarbeiter_id] = e; });
    return m;
  }, [effektivData]);

  // Wave sorting
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const sortedWaves = useMemo(() => {
    const arr = [...waves];
    arr.sort((a, b) => {
      let va, vb;
      switch (sortCol) {
        case 'name': va = a.name || ''; vb = b.name || ''; break;
        case 'start_time': va = a.start_time || ''; vb = b.start_time || ''; break;
        case 'employee': va = a.employee?.name || ''; vb = b.employee?.name || ''; break;
        case 'picks': va = a.picks || 0; vb = b.picks || 0; break;
        case 'articles': va = a.articles || 0; vb = b.articles || 0; break;
        case 'skus': va = a.skus || 0; vb = b.skus || 0; break;
        case 'duration': va = a.duration || 0; vb = b.duration || 0; break;
        case 'avg_pick': va = avgMin(a.duration, a.picks) ?? 999; vb = avgMin(b.duration, b.picks) ?? 999; break;
        default: va = a.start_time || ''; vb = b.start_time || '';
      }
      if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return arr;
  }, [waves, sortCol, sortDir]);

  const sortedPacks = useMemo(() => {
    const arr = [...packs];
    arr.sort((a, b) => {
      let va, vb;
      switch (sortCol) {
        case 'name': va = a.picking_name || ''; vb = b.picking_name || ''; break;
        case 'start_time': va = a.start_time || ''; vb = b.start_time || ''; break;
        case 'employee': va = a.employee?.name || ''; vb = b.employee?.name || ''; break;
        case 'articles': va = a.articles || 0; vb = b.articles || 0; break;
        case 'skus': va = a.skus || 0; vb = b.skus || 0; break;
        case 'packages': va = a.packages || 0; vb = b.packages || 0; break;
        case 'duration': va = a.duration || 0; vb = b.duration || 0; break;
        default: va = a.start_time || ''; vb = b.start_time || '';
      }
      if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return arr;
  }, [packs, sortCol, sortDir]);

  // ── Active filter count ──
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selEmployees.length > 0) count++;
    if (selMerchants.length > 0) count++;
    if (filterCrossDayWaves) count++;
    if (filterMaxDurEnabled) count++;
    if (filterWaveTypes.length > 0) count++;
    if (filterWaveNameEnabled && filterWaveNameContains.trim()) count++;
    return count;
  }, [selEmployees, selMerchants, filterCrossDayWaves, filterMaxDurEnabled, filterWaveTypes, filterWaveNameEnabled, filterWaveNameContains]);

  // Date display
  const dateLabel = useMemo(() => {
    if (!von || !bis) return '';
    const v = new Date(von + 'T12:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    if (von === bis) return v;
    const b = new Date(bis + 'T12:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return `${v} – ${b}`;
  }, [von, bis]);

  const SortHeader = ({ col, children, align = 'left' }) => (
    <th
      className={`py-3 cursor-pointer select-none hover:text-content-secondary transition-colors ${align === 'right' ? 'text-right' : 'text-left'} ${col === 'name' ? 'pl-4' : ''} ${col === 'avg_pick' ? 'pr-4' : ''}`}
      onClick={() => toggleSort(col)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortCol === col && (
          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d={sortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
          </svg>
        )}
      </span>
    </th>
  );

  // Active summary based on mode
  const activeSummary = mode === 'pack' ? packSummary : summary;
  const activeDaily = mode === 'pack' ? packDaily : daily;

  const resetAllFilters = () => {
    setSelEmployees([]);
    setSelMerchants([]);
    setFilterCrossDayWaves(false);
    setFilterMaxDurEnabled(false);
    setFilterWaveTypes([]);
    setFilterWaveNameEnabled(false);
    setFilterWaveNameContains('');
  };

  // ── Render ──
  return (
    <div className="p-page">
      {/* Header */}
      <PageHeader
        title={t('performance.title')}
        subtitle={t('performance.subtitle')}
        actions={<div className="flex items-center gap-3">
          {/* Pick / Pack / Both Toggle */}
          <div className="flex bg-surface-inset rounded-lg p-0.5 border border-line-hover">
            {[
              { key: 'pick', label: t('performance.pick'), icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' },
              { key: 'pack', label: t('performance.pack'), icon: 'M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z' },
              { key: 'both', label: t('performance.both') },
            ].map(m => (
              <button key={m.key} onClick={() => { setMode(m.key); setActiveTab(m.key === 'both' ? 'leaderboard' : 'waves'); }}
                className={`px-3 py-1.5 rounded-lg text-xs transition-colors flex items-center gap-1.5 ${
                  mode === m.key
                    ? 'bg-surface-card text-content font-semibold shadow-sm'
                    : 'text-[var(--text-secondary)] font-normal hover:text-content-secondary'
                }`}>
                {m.icon && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={m.icon} /></svg>
                )}
                {m.label}
              </button>
            ))}
          </div>

          {warehouses.length > 0 && (
            <select value={warehouse} onChange={e => setWarehouse(e.target.value)}
              className="bg-surface-inset border border-line-hover rounded-lg px-3 py-1.5 text-sm text-content focus:outline-none">
              <option value="">{t('performance.allLocations')}</option>
              {warehouses.map(w => <option key={w.warehouse_id} value={w.warehouse_id}>{w.warehouse_name}</option>)}
            </select>
          )}
        </div>}
      />

      {/* ── Time Filter Bar ───────────────────────────── */}
      <DateRangeFilter
        von={von} bis={bis} preset={preset}
        onChange={handleRangeChange}
        className="mb-2"
      >
        <div className="ml-auto text-[10px] text-[var(--text-secondary)] font-mono">{dateLabel}</div>
      </DateRangeFilter>

      {/* ── Search & Filter Bar ───────────────────────── */}
      <div className="flex flex-wrap items-center gap-2 mb-section p-3 bg-surface-card border border-line rounded-card">
        <div className="flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 text-[var(--text-secondary)]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
          <span className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase">{t('performance.employee')}</span>
        </div>
        <MultiSelect options={allEmployees} selected={selEmployees} onChange={setSelEmployees} placeholder={t('performance.allEmployees')} />

        <div className="w-px h-6 bg-line mx-1" />

        <div className="flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 text-[var(--text-secondary)]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
          <span className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase">{t('performance.customer')}</span>
        </div>
        <MultiSelect options={allMerchants} selected={selMerchants} onChange={setSelMerchants} placeholder={t('performance.allCustomers')} />

        <div className="w-px h-6 bg-line mx-1" />

        {/* Filter panel toggle */}
        <div className="relative">
          <button onClick={() => setShowFilterPanel(!showFilterPanel)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5 border ${
              showFilterPanel || activeFilterCount > 0
                ? 'bg-primary-500/15 text-primary-500 border-primary-500/30'
                : 'bg-surface-inset text-[var(--text-secondary)] hover:text-content-secondary border-transparent'
            }`}>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
            Filter
            {activeFilterCount > 0 && (
              <span className="bg-primary-500/30 text-primary-400 text-[10px] px-1.5 py-0.5 rounded-full font-mono font-bold">{activeFilterCount}</span>
            )}
          </button>

          {showFilterPanel && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowFilterPanel(false)} />
              <div className="absolute right-0 top-full mt-2 w-80 bg-surface-elevated border border-line-hover rounded-card shadow-sm z-50 p-4 space-y-3">
                <div className="text-xs font-semibold text-content mb-2">{t('performance.outlierFilter')}</div>

                <label className="flex items-center gap-2 text-xs text-content-secondary cursor-pointer">
                  <input type="checkbox" checked={filterCrossDayWaves} onChange={e => setFilterCrossDayWaves(e.target.checked)}
                    className="rounded accent-primary-500" />
                  {t('performance.crossDayWaves')}
                </label>

                <label className="flex items-center gap-2 text-xs text-content-secondary cursor-pointer">
                  <input type="checkbox" checked={filterMaxDurEnabled} onChange={e => setFilterMaxDurEnabled(e.target.checked)}
                    className="rounded accent-primary-500" />
                  <span>{t('performance.durationExclude')}</span>
                  <input type="number" value={filterMaxDuration} onChange={e => setFilterMaxDuration(Number(e.target.value))}
                    className="w-16 bg-surface-inset border border-line-hover rounded px-2 py-0.5 text-xs text-content focus:outline-none"
                    min="1" />
                  <span>{t('performance.minExclude')}</span>
                </label>

                {waveTypes.length > 0 && (
                  <div>
                    <div className="text-[10px] text-[var(--text-secondary)] font-semibold mb-1.5">{t('performance.waveTypesOnly')}</div>
                    <div className="flex flex-wrap gap-1">
                      {waveTypes.map(t => (
                        <button key={t}
                          onClick={() => setFilterWaveTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])}
                          className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                            filterWaveTypes.includes(t)
                              ? 'bg-primary-500/20 text-primary-500 border border-primary-500/30'
                              : 'bg-surface-inset text-[var(--text-secondary)] hover:text-content-secondary border border-transparent'
                          }`}>
                          {t}
                        </button>
                      ))}
                      {filterWaveTypes.length > 0 && (
                        <button onClick={() => setFilterWaveTypes([])}
                          className="px-2 py-0.5 text-[10px] text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]">{t('performance.all')}</button>
                      )}
                    </div>
                  </div>
                )}

                <label className="flex items-center gap-2 text-xs text-content-secondary cursor-pointer">
                  <input type="checkbox" checked={filterWaveNameEnabled} onChange={e => setFilterWaveNameEnabled(e.target.checked)}
                    className="rounded accent-primary-500" />
                  <span>{t('performance.waveNameContains')}</span>
                  <input type="text" value={filterWaveNameContains} onChange={e => setFilterWaveNameContains(e.target.value)}
                    placeholder={t('performance.egSOP')}
                    className="flex-1 bg-surface-inset border border-line-hover rounded px-2 py-0.5 text-xs text-content placeholder-content-dim focus:outline-none" />
                </label>

                {activeFilterCount > 0 && (
                  <button onClick={resetAllFilters}
                    className="w-full text-xs text-red-400 hover:text-red-300 py-1.5 border-t border-line mt-2 pt-3">
                    {t('performance.resetAllFilters')}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Excluded count banner removed — filtering is now server-side */}

      {/* Error state */}
      {error && (
        <div className="mb-section p-3 bg-red-500/10 border border-red-500/20 rounded-card text-sm text-red-400">
          {t('performance.apiError', { error })}
        </div>
      )}

      {/* No-data hint */}
      {!loading && !error && summary.totalWaves === 0 && dataRange?.last_date && (
        <div className="mb-section flex items-center gap-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-card text-sm">
          <svg className="w-5 h-5 text-amber-400 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" /><path d="M12 8v4m0 4h.01" />
          </svg>
          <span className="text-content-secondary">
            {t('performance.noWavesInPeriod')}{' '}
            <strong>{new Date(dataRange.last_date + 'T12:00:00').toLocaleDateString('de-DE')}</strong>{' '}
            ({t('performance.wavesTotal', { n: fmt(dataRange.total) })}).
          </span>
          <button onClick={jumpToLastData}
            className="ml-auto text-xs text-amber-400 hover:text-amber-300 font-medium whitespace-nowrap">
            {t('performance.showAllData')}
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full" />
          <span className="ml-3 text-sm text-[var(--text-secondary)]">{t('performance.loadingData')}</span>
        </div>
      )}

      {/* Main content */}
      {!loading && data && (
        <>
          {/* KPI Cards */}
          {mode === 'pick' && (
            <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-3 mb-section">
              <KpiCard label={t('performance.waves')} value={fmt(summary.totalWaves)}
                tooltip={sopCounts.total > 0 ? `SOP: ${fmt(sopCounts.sop)} (${sopCounts.total > 0 ? Math.round(sopCounts.sop / sopCounts.total * 100) : 0}%)\n${t('performance.normal')}: ${fmt(sopCounts.normal)} (${sopCounts.total > 0 ? Math.round(sopCounts.normal / sopCounts.total * 100) : 0}%)\n${t('performance.urgent')}: ${fmt(sopCounts.dringend)} (${sopCounts.total > 0 ? Math.round(sopCounts.dringend / sopCounts.total * 100) : 0}%)` : null} />
              <KpiCard label={t('performance.pickings')} value={fmt(summary.totalPicks)} tooltip={t('performance.pickingsTooltip')} />
              <KpiCard label={t('performance.articles')} value={fmt(summary.totalArticles)} />
              <KpiCard label={t('performance.skus')} value={fmt(summary.totalSkus)} />
              <KpiCard label={t('performance.avgDuration')} value={formatMinutes(summary.avgDuration)} />
              <KpiCard label={t('performance.artPerH')} value={summary.articlesPerHour || 0} />
              <KpiCard label={t('performance.activeEmployees')} value={summary.activeEmployees || 0} />
            </div>
          )}

          {mode === 'pack' && (
            <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-3 mb-section">
              <KpiCard label={t('performance.packs')} value={fmt(packSummary.totalPacks)} />
              <KpiCard label={t('performance.packages')} value={fmt(packSummary.totalPackages)} />
              <KpiCard label={t('performance.articles')} value={fmt(packSummary.totalArticles)} />
              <KpiCard label={t('performance.skus')} value={fmt(packSummary.totalSkus)} />
              <KpiCard label={t('performance.avgDuration')} value={formatMinutes(packSummary.avgDuration)} />
              <KpiCard label={t('performance.artPerH')} value={packSummary.articlesPerHour || 0} />
              <KpiCard label={t('performance.activeEmployees')} value={packSummary.activeEmployees || 0} />
            </div>
          )}

          {mode === 'both' && (
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 mb-section">
              <KpiCard label={t('performance.pickArticles')} value={fmt(summary.totalArticles)} />
              <KpiCard label={t('performance.pickArtPerH')} value={summary.articlesPerHour || 0} />
              <KpiCard label={t('performance.waves')} value={fmt(summary.totalWaves)} />
              <KpiCard label={t('performance.packArticles')} value={fmt(packSummary.totalArticles)} />
              <KpiCard label={t('performance.packArtPerH')} value={packSummary.articlesPerHour || 0} />
              <KpiCard label={t('performance.packs')} value={fmt(packSummary.totalPacks)} />
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 mb-section border-b border-line overflow-x-auto">
            {mode !== 'both' && [
              { key: 'waves', label: mode === 'pick' ? t('performance.wavesTab') : t('performance.packsTab'), badge: mode === 'pick' ? totalWaveCount : totalPackCount },
              { key: 'leaderboard', label: t('performance.leaderboard'), badge: mode === 'pick' ? leaderboard.length : packLeaderboard.length },
              { key: 'trend', label: t('performance.trend') },
              { key: 'durchlaufzeit', label: t('performance.durchlaufzeit') },
              { key: 'idle_time', label: 'Idle-Time' },
              { key: 'effizienz', label: t('efficiency.title') },
              { key: 'einstellungen', label: t('modulSettings.title', 'Einstellungen') },
            ].map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
                  activeTab === t.key ? 'text-content border-b-2 border-primary-500' : 'text-[var(--text-secondary)] hover:text-content-secondary'
                }`}>
                {t.label}
                {t.badge != null && t.badge > 0 && (
                  <Badge variant="default">{t.badge > 999 ? `${Math.round(t.badge / 100) / 10}k` : t.badge}</Badge>
                )}
              </button>
            ))}
            {mode === 'both' && [
              { key: 'leaderboard', label: t('performance.leaderboard') },
              { key: 'trend', label: t('performance.trend') },
              { key: 'durchlaufzeit', label: t('performance.durchlaufzeit') },
              { key: 'idle_time', label: 'Idle-Time' },
              { key: 'effizienz', label: t('efficiency.title') },
              { key: 'einstellungen', label: t('modulSettings.title', 'Einstellungen') },
            ].map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
                  activeTab === t.key ? 'text-content border-b-2 border-primary-500' : 'text-[var(--text-secondary)] hover:text-content-secondary'
                }`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-surface-card border border-line rounded-card p-5 shadow-card">

            {/* ── Pick Waves Tab ── */}
            {activeTab === 'waves' && mode === 'pick' && (
              <>
                {/* SOP Filter Toggle */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex bg-surface-inset rounded-lg p-0.5 border border-line-hover">
                    {[
                      { key: 'alle', label: t('performance.all'), count: sopCounts.total },
                      { key: 'sop', label: t('performance.sop'), count: sopCounts.sop, color: 'text-cyan-400' },
                      { key: 'normal', label: t('performance.normal'), count: sopCounts.normal },
                      { key: 'dringend', label: t('performance.urgent'), count: sopCounts.dringend, color: 'text-red-400' },
                    ].map(f => (
                      <button key={f.key} onClick={() => setSopWaveFilter(f.key)}
                        className={`px-2.5 py-1 rounded text-[10px] font-medium transition-colors flex items-center gap-1.5 ${
                          sopWaveFilter === f.key ? 'bg-surface-card text-content font-semibold shadow-sm' : 'text-[var(--text-secondary)] font-normal hover:text-content-secondary'
                        }`}>
                        <span className={sopWaveFilter === f.key && f.color ? f.color : ''}>{f.label}</span>
                        <span className={`font-mono text-[9px] ${sopWaveFilter === f.key ? 'text-content-secondary' : 'text-[var(--text-tertiary)]'}`}>
                          {fmt(f.count)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                {waves.length === 0
                ? <div className="text-sm text-[var(--text-tertiary)] py-8 text-center">{t('performance.noWaves')}</div>
                : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
                          <SortHeader col="name">{t('performance.wave')}</SortHeader>
                          <SortHeader col="start_time">{t('performance.date')}</SortHeader>
                          <SortHeader col="employee">{t('performance.ma')}</SortHeader>
                          <SortHeader col="picks" align="right">{t('performance.pickings')}</SortHeader>
                          <SortHeader col="articles" align="right">{t('performance.articles')}</SortHeader>
                          <SortHeader col="skus" align="right">{t('performance.skus')}</SortHeader>
                          <SortHeader col="duration" align="right">{t('performance.duration')}</SortHeader>
                          <SortHeader col="avg_pick" align="right">{t('performance.avgPerPick')}</SortHeader>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedWaves.map(w => {
                          const isOutlier = w.duration > 0 && w.duration > waveStats.mean + 2 * waveStats.sd;
                          return (
                          <Fragment key={w.id}>
                            <tr onClick={() => setExpanded(expanded === w.id ? null : w.id)}
                              className={`border-b border-line hover:bg-surface-hover cursor-pointer transition-colors ${isOutlier ? 'bg-amber-500/10' : ''}`}
                              title={isOutlier ? t('performance.outlierTitle', { duration: formatMinutes(w.duration), avg: formatMinutes(waveStats.mean) }) : undefined}>
                              <td className="py-2.5 pl-4">
                                <div className="flex items-center gap-2">
                                  <svg className={`w-3.5 h-3.5 text-[var(--text-secondary)] transition-transform shrink-0 ${expanded === w.id ? 'rotate-90' : ''}`}
                                    fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" /></svg>
                                  <span className="font-mono text-xs font-bold text-content">{w.name}</span>
                                  {waveType(w.name) === 'sop' && <Badge variant="info">SOP</Badge>}
                                  {waveType(w.name) === 'dringend' && <Badge variant="error">DRINGEND</Badge>}
                                  {w.warehouse && <Badge variant="default">{w.warehouse}</Badge>}
                                  {isSharedAccount(w.employee?.name) && <Badge variant="default" title="Shared Account">&#128101; {t('performance.shared')}</Badge>}
                                  {isOutlier && <Badge variant="warning">{t('performance.outlier')}</Badge>}
                                </div>
                              </td>
                              <td className="py-2.5 text-xs text-content-secondary font-mono">
                                {new Date(w.start_time).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                              </td>
                              <td className="py-2.5 text-xs text-content-secondary">{w.employee?.name || t('performance.unknown')}</td>
                              <td className="py-2.5 text-right font-mono text-xs text-content-secondary">{w.picks}</td>
                              <td className="py-2.5 text-right font-mono text-xs font-bold text-content">{fmt(w.articles)}</td>
                              <td className="py-2.5 text-right font-mono text-xs text-content-secondary">{fmt(w.skus)}</td>
                              <td className="py-2.5 text-right font-mono text-xs text-content-tertiary">{formatMinutes(w.duration)}</td>
                              <td className="py-2.5 text-right pr-4 font-mono text-xs font-bold text-content">{formatMinutes(avgMin(w.duration, w.picks))}</td>
                            </tr>
                            {expanded === w.id && w.pickings?.length > 0 && (
                              <tr className="border-b border-line">
                                <td colSpan={8} className="bg-surface-inset/50 px-6 py-3">
                                  <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-2 flex items-center gap-2">
                                    <span>{t('performance.pickings')} ({w.pickings.length})</span>
                                    {waveType(w.name) === 'sop' && <Badge variant="info">{t('performance.sopWave')}</Badge>}
                                    {waveType(w.name) === 'dringend' && <Badge variant="error">{t('performance.urgentWave')}</Badge>}
                                    {isSharedAccount(w.employee?.name) && <Badge variant="default">&#128101; {w.employee?.name} (Shared)</Badge>}
                                  </div>
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="text-[10px] text-[var(--text-tertiary)] border-b border-line/50">
                                        <th className="py-1.5 text-left">{t('performance.picking')}</th>
                                        <th className="py-1.5 text-left">{t('performance.merchant')}</th>
                                        <th className="py-1.5 text-right">{t('performance.skus')}</th>
                                        <th className="py-1.5 text-right">{t('performance.articles')}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {w.pickings.map((p, i) => (
                                        <tr key={i} className="border-b border-line/30">
                                          <td className="py-1.5 font-mono text-content-secondary">{p.picking || '–'}</td>
                                          <td className="py-1.5 text-content-secondary">{p.merchant || t('performance.unknown')}</td>
                                          <td className="py-1.5 text-right font-mono text-[var(--text-tertiary)]">{p.skus}</td>
                                          <td className="py-1.5 text-right font-mono font-bold text-content">{p.articles}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                    {totalWaveCount > waves.length && (
                      <div className="mt-3 text-xs text-[var(--text-tertiary)] text-center py-2">
                        {t('performance.showingOf', { shown: waves.length, total: fmt(totalWaveCount), type: t('performance.waves') })}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* ── Pack Tab ── */}
            {activeTab === 'waves' && mode === 'pack' && (
              packs.length === 0
                ? <div className="text-sm text-[var(--text-tertiary)] py-8 text-center">{t('performance.noPacks')}</div>
                : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
                          <SortHeader col="name">{t('performance.picking')}</SortHeader>
                          <SortHeader col="start_time">{t('performance.date')}</SortHeader>
                          <SortHeader col="employee">{t('performance.ma')}</SortHeader>
                          <th className="py-3 text-left text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">{t('performance.merchant')}</th>
                          <SortHeader col="articles" align="right">{t('performance.articles')}</SortHeader>
                          <SortHeader col="skus" align="right">{t('performance.skus')}</SortHeader>
                          <SortHeader col="packages" align="right">{t('performance.packages')}</SortHeader>
                          <SortHeader col="duration" align="right">{t('performance.duration')}</SortHeader>
                          <th className="py-3 text-right text-[10px] text-[var(--text-secondary)] uppercase tracking-wider pr-4">{t('performance.gewicht')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedPacks.map(p => {
                          const isOutlier = p.duration > 0 && p.duration > packStats.mean + 2 * packStats.sd;
                          return (
                          <tr key={p.id}
                            className={`border-b border-line hover:bg-surface-hover transition-colors ${isOutlier ? 'bg-amber-500/10' : ''}`}
                            title={isOutlier ? t('performance.outlierTitle', { duration: formatMinutes(p.duration), avg: formatMinutes(packStats.mean) }) : undefined}>
                            <td className="py-2.5 pl-4">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-xs font-bold text-content">{p.picking_name}</span>
                                {isOutlier && <Badge variant="warning">{t('performance.outlier')}</Badge>}
                              </div>
                            </td>
                            <td className="py-2.5 text-xs text-content-secondary font-mono">
                              {new Date(p.start_time).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td className="py-2.5 text-xs text-content-secondary">{p.employee?.name || t('performance.unknown')}</td>
                            <td className="py-2.5 text-xs text-content-tertiary truncate max-w-[150px]">{p.merchant || '–'}</td>
                            <td className="py-2.5 text-right font-mono text-xs font-bold text-content">{fmt(p.articles)}</td>
                            <td className="py-2.5 text-right font-mono text-xs text-content-secondary">{fmt(p.skus)}</td>
                            <td className="py-2.5 text-right font-mono text-xs text-content font-bold">{p.packages}</td>
                            <td className="py-2.5 text-right font-mono text-xs text-content-tertiary">{formatMinutes(p.duration)}</td>
                            <td className="py-2.5 text-right pr-4 font-mono text-xs text-content-secondary">{p.total_weight != null ? `${Number(p.total_weight).toFixed(2)} kg` : '–'}</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {totalPackCount > packs.length && (
                      <div className="mt-3 text-xs text-[var(--text-tertiary)] text-center py-2">
                        {t('performance.showingOf', { shown: packs.length, total: fmt(totalPackCount), type: t('performance.packsTab') })}
                      </div>
                    )}
                  </div>
                )
            )}

            {/* ── Leaderboard Tab (Pick or Pack) ── */}
            {activeTab === 'leaderboard' && mode !== 'both' && (
              <>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  {/* SOP sub-filter (pick mode only) */}
                  {mode === 'pick' && (
                    <div className="flex bg-surface-inset rounded-lg p-0.5 border border-line-hover">
                      {[
                        { key: 'alle', label: t('performance.totalOnly') },
                        { key: 'normal', label: t('performance.normalOnly') },
                        { key: 'sop', label: t('performance.sopOnly') },
                      ].map(f => (
                        <button key={f.key} onClick={() => setLbSopFilter(f.key)}
                          className={`px-2.5 py-1 rounded text-[10px] font-medium transition-colors ${
                            lbSopFilter === f.key ? 'bg-surface-card text-content font-semibold shadow-sm' : 'text-[var(--text-secondary)] font-normal hover:text-content-secondary'
                          }`}>{f.label}</button>
                      ))}
                    </div>
                  )}

                  {/* Shared accounts toggle */}
                  <label className="flex items-center gap-1.5 text-[10px] text-[var(--text-secondary)] cursor-pointer select-none">
                    <input type="checkbox" checked={showSharedAccounts} onChange={e => setShowSharedAccounts(e.target.checked)}
                      className="rounded accent-emerald-500 w-3 h-3" />
                    &#128101; {t('performance.sharedAccounts', { n: sharedAccountCount })}
                  </label>

                  {/* Search */}
                  {filteredLeaderboard.length > 3 && (
                    <div className="relative w-48 ml-auto">
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--text-secondary)]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                      <input type="text" value={searchMa} onChange={e => setSearchMa(e.target.value)}
                        placeholder={t('performance.searchEmployee')}
                        className="pl-8 pr-3 py-1.5 bg-surface-inset border border-line-hover rounded-lg text-xs text-content placeholder-content-dim focus:outline-none w-full" />
                    </div>
                  )}
                </div>

                {filteredLeaderboard.length === 0
                  ? <div className="text-sm text-[var(--text-tertiary)] py-8 text-center">{t('performance.noEmployeeData')}</div>
                  : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
                            <th className="py-3 pl-4 text-left w-12">#</th>
                            <th className="py-3 text-left">{t('performance.employeeCol')}</th>
                            <th className="py-3 text-right">{mode === 'pick' ? t('performance.waves') : t('performance.packsCol')}</th>
                            {mode === 'pick' && <th className="py-3 text-right">{t('performance.pickings')}</th>}
                            {mode === 'pack' && <th className="py-3 text-right">{t('performance.packages')}</th>}
                            <th className="py-3 text-right">{t('performance.articles')}</th>
                            <th className="py-3 text-right">{t('performance.artPerH')}</th>
                            {mode === 'pick' && lbSopFilter === 'alle' && <th className="py-3 text-right">SOP%</th>}
                            {mode === 'pack' && <th className="py-3 text-right">{t('performance.gesamtGewicht')}</th>}
                            {mode === 'pack' && <th className="py-3 text-right">{t('performance.avgGewicht')}</th>}
                            {mode === 'pack' && <th className="py-3 text-right">{t('performance.kgProMin')}</th>}
                            <th className="py-3 text-right">{t('performance.avgDuration')}</th>
                            <th className="py-3 text-right pr-4">{t('performance.effectivePct')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredLeaderboard.map(ma => (
                            <tr key={ma.mitarbeiter_id}
                              onClick={() => mode === 'pick' ? setSelectedMa(ma.mitarbeiter_id) : null}
                              className={`border-b border-line hover:bg-surface-hover ${mode === 'pick' ? 'cursor-pointer' : ''} transition-colors`}>
                              <td className="py-3 pl-4">
                                <span className="font-mono font-bold text-xs text-[var(--text-secondary)]">{ma.rank}</span>
                              </td>
                              <td className="py-3">
                                <div className="flex items-center gap-2">
                                  {ma.image
                                    ? <img src={ma.image} className="w-7 h-7 rounded-full object-cover" alt="" />
                                    : <div className="w-7 h-7 rounded-full bg-surface-active flex items-center justify-center text-[10px] font-bold text-content-tertiary">{ma.kuerzel || ma.name?.slice(0, 2).toUpperCase()}</div>
                                  }
                                  <span className="font-medium text-content">{ma.name}</span>
                                  {ma.shared && <Badge variant="default" title="Shared Account">&#128101; Shared</Badge>}
                                </div>
                              </td>
                              <td className="py-3 text-right font-mono text-xs text-content-secondary">{ma.waves || ma.packs}</td>
                              <td className="py-3 text-right font-mono text-xs text-content-secondary">{mode === 'pick' ? fmt(ma.picks) : (ma.packages || 0)}</td>
                              <td className="py-3 text-right font-mono text-xs font-bold text-content">{fmt(ma.articles)}</td>
                              <td className="py-3 text-right">
                                <span className="font-mono text-xs font-bold text-content">
                                  {ma.articles_per_hour}
                                </span>
                              </td>
                              {mode === 'pick' && lbSopFilter === 'alle' && (
                                <td className="py-3 text-right font-mono text-xs">
                                  {ma.sop_pct > 0 ? (
                                    <span className="text-[var(--text-primary)]">{ma.sop_pct}%</span>
                                  ) : (
                                    <span className="text-[var(--text-tertiary)]">–</span>
                                  )}
                                </td>
                              )}
                              {mode === 'pack' && (
                                <td className="py-3 text-right font-mono text-xs font-bold text-content">
                                  {ma.gesamt_gewicht > 0 ? `${ma.gesamt_gewicht.toFixed(1)} kg` : <span className="text-[var(--text-tertiary)]">–</span>}
                                </td>
                              )}
                              {mode === 'pack' && (
                                <td className="py-3 text-right font-mono text-xs text-content-secondary">
                                  {ma.avg_weight > 0 ? `${ma.avg_weight.toFixed(2)} kg` : <span className="text-[var(--text-tertiary)]">–</span>}
                                </td>
                              )}
                              {mode === 'pack' && (
                                <td className="py-3 text-right font-mono text-xs text-content-secondary">
                                  {ma.kg_per_min > 0 ? ma.kg_per_min.toFixed(3) : <span className="text-[var(--text-tertiary)]">–</span>}
                                </td>
                              )}
                              <td className="py-3 text-right font-mono text-xs text-content-tertiary">{formatMinutes(ma.avg_duration)}</td>
                              <td className="py-3 text-right pr-4 font-mono text-xs">
                                {ma.shared ? <span className="text-[var(--text-tertiary)]">n/a</span> : (() => {
                                  const eff = effektivMap[ma.mitarbeiter_id];
                                  if (!eff || !eff.anwesenheit_min) return <span className="text-[var(--text-tertiary)]">{'–'}</span>;
                                  const pct = eff.effektiv_pct;
                                  const dotColor = pct >= 80 ? '#34d399' : pct >= 60 ? '#eab308' : pct >= 40 ? '#f97316' : '#ef4444';
                                  return <span className="inline-flex items-center gap-1.5 font-bold text-[var(--text-primary)]"><span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: dotColor }} />{pct.toFixed(1)}%</span>;
                                })()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                }
              </>
            )}

            {/* ── Combined Leaderboard (Both mode) ── */}
            {activeTab === 'leaderboard' && mode === 'both' && (
              <>
                {combinedLeaderboard.length > 3 && (
                  <div className="mb-4">
                    <div className="relative w-56">
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-secondary)]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                      <input type="text" value={searchMa} onChange={e => setSearchMa(e.target.value)}
                        placeholder={t('performance.searchEmployee')}
                        className="pl-9 pr-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content placeholder-content-dim focus:outline-none w-full" />
                    </div>
                  </div>
                )}
                {combinedLeaderboard.length === 0
                  ? <div className="text-sm text-[var(--text-tertiary)] py-8 text-center">{t('performance.noDataInPeriod')}</div>
                  : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
                            <th className="py-3 pl-4 text-left w-12">#</th>
                            <th className="py-3 text-left">{t('performance.employeeCol')}</th>
                            <th className="py-3 text-right">{t('performance.pickArt')}</th>
                            <th className="py-3 text-right">{t('performance.pickArtH2')}</th>
                            <th className="py-3 text-right">{t('performance.packArt')}</th>
                            <th className="py-3 text-right">{t('performance.packArtH2')}</th>
                            <th className="py-3 text-right">{t('performance.totalArt')}</th>
                            <th className="py-3 text-right pr-4">{t('performance.effectivePct')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {combinedLeaderboard.map(ma => (
                            <tr key={ma.mitarbeiter_id}
                              className="border-b border-line hover:bg-surface-hover transition-colors">
                              <td className="py-3 pl-4">
                                <span className="font-mono font-bold text-xs text-[var(--text-secondary)]">{ma.rank}</span>
                              </td>
                              <td className="py-3">
                                <div className="flex items-center gap-2">
                                  {ma.image
                                    ? <img src={ma.image} className="w-7 h-7 rounded-full object-cover" alt="" />
                                    : <div className="w-7 h-7 rounded-full bg-surface-active flex items-center justify-center text-[10px] font-bold text-content-tertiary">{ma.kuerzel || ma.name?.slice(0, 2).toUpperCase()}</div>
                                  }
                                  <span className="font-medium text-content">{ma.name}</span>
                                </div>
                              </td>
                              <td className="py-3 text-right font-mono text-xs text-content font-bold">{fmt(ma.pick_articles)}</td>
                              <td className="py-3 text-right">
                                <span className="font-mono text-xs font-bold text-content">
                                  {ma.pick_aph || '–'}
                                </span>
                              </td>
                              <td className="py-3 text-right font-mono text-xs text-content font-bold">{fmt(ma.pack_articles)}</td>
                              <td className="py-3 text-right">
                                <span className="font-mono text-xs font-bold text-content">
                                  {ma.pack_aph || '–'}
                                </span>
                              </td>
                              <td className="py-3 text-right font-mono text-xs font-bold text-content">{fmt(ma.total_articles)}</td>
                              <td className="py-3 text-right pr-4 font-mono text-xs">
                                {ma.shared ? <span className="text-[var(--text-tertiary)]">n/a</span> : (() => {
                                  const eff = effektivMap[ma.mitarbeiter_id];
                                  if (!eff || !eff.anwesenheit_min) return <span className="text-[var(--text-tertiary)]">{'–'}</span>;
                                  const pct = eff.effektiv_pct;
                                  const dotColor = pct >= 80 ? '#34d399' : pct >= 60 ? '#eab308' : pct >= 40 ? '#f97316' : '#ef4444';
                                  return <span className="inline-flex items-center gap-1.5 font-bold text-[var(--text-primary)]"><span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: dotColor }} />{pct.toFixed(1)}%</span>;
                                })()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                }
              </>
            )}

            {/* ── Trend Tab ── */}
            {activeTab === 'trend' && mode !== 'both' && (
              activeDaily.length < 2
                ? <div className="text-sm text-[var(--text-tertiary)] py-8 text-center">{t('performance.notEnoughDays')}</div>
                : (
                  <div className="space-y-6">
                    <TrendChart data={activeDaily} dataKey="articles" label={mode === 'pick' ? t('performance.pickArtPerDay') : t('performance.packArtPerDay')} color={PERF} t={t} />
                    <TrendChart data={activeDaily} dataKey="articles_per_hour" label={t('performance.artPerHour')} color={PURPLE} t={t} />
                    <TrendChart data={activeDaily} dataKey={mode === 'pick' ? 'waves' : 'packs'} label={mode === 'pick' ? t('performance.wavesPerDay') : t('performance.packsPerDay')} color={BLUE} t={t} />
                    <TrendChart data={activeDaily} dataKey="employees" label={t('performance.activeEmpl')} color={WARN} t={t} />
                  </div>
                )
            )}

            {/* ── Trend Tab (Both mode) ── */}
            {activeTab === 'trend' && mode === 'both' && (
              (daily.length < 2 && packDaily.length < 2)
                ? <div className="text-sm text-[var(--text-tertiary)] py-8 text-center">{t('performance.notEnoughDays')}</div>
                : (
                  <div className="space-y-6">
                    {daily.length >= 2 && <TrendChart data={daily} dataKey="articles" label={t('performance.pickArtDay')} color={PERF} t={t} />}
                    {packDaily.length >= 2 && <TrendChart data={packDaily} dataKey="articles" label={t('performance.packArtDay')} color="#f472b6" t={t} />}
                    {daily.length >= 2 && <TrendChart data={daily} dataKey="articles_per_hour" label={t('performance.pickArtH')} color={PURPLE} t={t} />}
                    {packDaily.length >= 2 && <TrendChart data={packDaily} dataKey="articles_per_hour" label={t('performance.packArtH')} color={WARN} t={t} />}
                  </div>
                )
            )}

            {/* ── Durchlaufzeit Tab ── */}
            {activeTab === 'durchlaufzeit' && (
              <div className="space-y-6">
                {/* KPI Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="border border-line rounded-card p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock size={20} className="text-[#22D3EE]" />
                      <div className="text-[10px] text-content-secondary uppercase tracking-wider font-semibold">{t('performance.medianDurchlaufzeit')}</div>
                    </div>
                    <div className="text-2xl font-bold text-content">{durchlaufzeitData?.median_stunden?.toFixed(1) ?? '–'}h</div>
                    <div className="text-[10px] text-content-tertiary mt-1" title={t('performance.medianHint')}>{t('performance.medianHint')}</div>
                  </div>
                  <div className="border border-line rounded-card p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Timer size={20} className="text-[#22D3EE]" />
                      <div className="text-[10px] text-content-secondary uppercase tracking-wider font-semibold">{t('performance.avgDurchlaufzeit')}</div>
                    </div>
                    <div className="text-2xl font-bold text-content">{durchlaufzeitData?.avg_stunden?.toFixed(1) ?? '–'}h</div>
                    <div className="text-[10px] text-content-tertiary mt-1">{t('performance.durchlaufzeitHint')}</div>
                  </div>
                  <div className="border border-line rounded-card p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Gauge size={20} className="text-[#338edc]" />
                      <div className="text-[10px] text-content-secondary uppercase tracking-wider font-semibold">{t('performance.minDurchlaufzeit')}</div>
                    </div>
                    <div className="text-2xl font-bold text-content">{durchlaufzeitData?.min_stunden?.toFixed(1) ?? '–'}h</div>
                  </div>
                  <div className="border border-line rounded-card p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Package size={20} className="text-[#338edc]" />
                      <div className="text-[10px] text-content-secondary uppercase tracking-wider font-semibold">{t('performance.packsWithWeight')}</div>
                    </div>
                    <div className="text-2xl font-bold text-content">{durchlaufzeitData?.anzahl ? fmt(durchlaufzeitData.anzahl) : '–'}</div>
                    <div className="text-[10px] text-content-tertiary mt-1">{t('performance.basedOnDays', { days: 30 })}</div>
                  </div>
                </div>

                {/* Trend Chart */}
                {durchlaufzeitTrend.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="w-4 h-4 text-content-secondary" />
                      <h3 className="text-sm font-bold text-content">{t('performance.durchlaufzeitSektion')}</h3>
                    </div>
                    <div style={{ height: 300 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={durchlaufzeitTrend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                          <defs>
                            <linearGradient id="gradDLZ" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#22D3EE" stopOpacity={0.15} />
                              <stop offset="100%" stopColor="#22D3EE" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary)" />
                          <XAxis dataKey="tag"
                            tickFormatter={d => { if (!d) return ''; const p = d.split('-'); return `${p[2]}.${p[1]}`; }}
                            tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                            axisLine={{ stroke: 'var(--border-primary)' }} tickLine={false} />
                          <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                            axisLine={{ stroke: 'var(--border-primary)' }} tickLine={false}
                            tickFormatter={v => `${v}h`} />
                          <Tooltip
                            content={({ active, payload, label }) => {
                              if (!active || !payload?.length) return null;
                              const d = payload[0]?.payload;
                              const dateParts = label ? label.split('-') : [];
                              const fmtLabel = dateParts.length === 3 ? `${dateParts[2]}.${dateParts[1]}.${dateParts[0]}` : label;
                              return (
                                <div className="bg-surface-elevated border border-line-hover rounded-card px-3 py-2 shadow-xl text-xs">
                                  <div className="font-medium text-content mb-1">{fmtLabel}</div>
                                  <div className="text-content-secondary">{t('performance.avgDurchlaufzeit')}: <span className="font-bold text-content">{d?.avg_stunden?.toFixed(1)}h</span></div>
                                  <div className="text-content-tertiary">{fmt(d?.pakete)} {t('performance.packs')}</div>
                                </div>
                              );
                            }}
                          />
                          <ReferenceLine y={24} stroke="#E5E7EB" strokeDasharray="6 3"
                            label={{ value: t('performance.benchmark1Tag'), position: 'right', fill: 'var(--text-muted)', fontSize: 10 }} />
                          <Area type="monotone" dataKey="avg_stunden" name={t('performance.avgDurchlaufzeit')}
                            stroke="#22D3EE" strokeWidth={2} fill="url(#gradDLZ)" dot={false} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Weight KPIs */}
                {packSummary.packsWithWeight > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Weight className="w-4 h-4 text-content-secondary" />
                      <h3 className="text-sm font-bold text-content">{t('performance.paketgewicht')}</h3>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="border border-line rounded-card p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Weight size={20} className="text-[#338edc]" />
                          <div className="text-[10px] text-content-secondary uppercase tracking-wider font-semibold">{t('performance.avgGewicht')}</div>
                        </div>
                        <div className="text-2xl font-bold text-content">{packSummary.avgWeight?.toFixed(2) ?? '–'} kg</div>
                      </div>
                      <div className="border border-line rounded-card p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Gauge size={20} className="text-[#338edc]" />
                          <div className="text-[10px] text-content-secondary uppercase tracking-wider font-semibold">{t('performance.gesamtGewicht')}</div>
                        </div>
                        <div className="text-2xl font-bold text-content">{packSummary.totalWeight ? fmt(Math.round(packSummary.totalWeight)) : '–'} kg</div>
                      </div>
                      <div className="border border-line rounded-card p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Package size={20} className="text-[#338edc]" />
                          <div className="text-[10px] text-content-secondary uppercase tracking-wider font-semibold">{t('performance.packsWithWeight')}</div>
                        </div>
                        <div className="text-2xl font-bold text-content">{fmt(packSummary.packsWithWeight)}</div>
                      </div>
                      {packSummary.totalWeight > 0 && packSummary.avgWeight > 100 && (
                        <div className="border border-amber-300/30 rounded-card p-4 bg-amber-500/5">
                          <div className="text-[10px] text-amber-500 uppercase tracking-wider font-semibold mb-1">{t('performance.ausreisserHinweis')}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Idle-Time Tab ── */}
            {activeTab === 'idle_time' && (
              <IdleTimeEmbed von={von} bis={bis} type={mode === 'both' ? 'pack' : mode} t={t} />
            )}

            {/* ── Effizienz Tab ── */}
            {activeTab === 'effizienz' && (
              <EffizienzTab von={von} bis={bis} type={mode === 'both' ? 'pack' : mode} />
            )}
          </div>

          {activeTab === 'einstellungen' && (
            <ModulEinstellungen kategorien={['berechnung', 'mitarbeiter_pulse']} />
          )}

          {/* Per-warehouse summary */}
          {data.warehouses?.length > 1 && mode !== 'both' && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {data.warehouses.map(wh => (
                <div key={wh.name} className="p-3 bg-surface-card border border-line rounded-card">
                  <div className="text-xs font-medium text-content mb-1">{wh.name}</div>
                  <div className="grid grid-cols-3 gap-1 text-[10px]">
                    <div><span className="text-[var(--text-tertiary)]">{t('performance.warehouseWaves')}</span> <span className="font-mono font-bold">{wh.waves}</span></div>
                    <div><span className="text-[var(--text-tertiary)]">{t('performance.warehousePickings')}</span> <span className="font-mono font-bold text-content">{fmt(wh.picks)}</span></div>
                    <div><span className="text-[var(--text-tertiary)]">{t('performance.warehouseArticles')}</span> <span className="font-mono font-bold text-content">{fmt(wh.articles)}</span></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* MA Detail Panel */}
      {selectedMa && <MaDetail maId={selectedMa} von={von} bis={bis} onClose={() => setSelectedMa(null)} t={t} />}
    </div>
  );
}

/* ─── Idle-Time Embed for Performance Tab ─── */
function IdleTimeEmbed({ von, bis, type, t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!von || !bis) return;
    setLoading(true);
    fetch(`${API}/api/reports/idle-time/summary?von=${von}&bis=${bis}&type=${type}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [von, bis, type]);

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full" /></div>;
  if (!data?.summary) return <div className="text-sm text-[var(--text-tertiary)] py-8 text-center">{t('performance.noData')}</div>;

  const s = data.summary;
  const lb = (data.leaderboard || []).slice(0, 15);
  const fMin = (m) => m != null ? Number(m).toFixed(1).replace('.', ',') + ' min' : '–';
  const fH = (h) => h != null ? Number(h).toFixed(1).replace('.', ',') + ' h' : '–';
  const fPct = (p) => p != null ? Number(p).toFixed(1).replace('.', ',') + '%' : '–';
  const fEur = (e) => e != null ? Number(e).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €' : '–';

  return (
    <div>
      {/* 4 KPI row */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="text-center"><div className="text-[11px] font-semibold uppercase text-[var(--text-secondary)] tracking-wider">{t('idleTime.avgIdle')}</div><div className="text-lg font-bold text-content">{fMin(s.avg_idle_minutes)}</div></div>
        <div className="text-center"><div className="text-[11px] font-semibold uppercase text-[var(--text-secondary)] tracking-wider">{t('idleTime.productivity')}</div><div className="text-lg font-bold text-content">{fPct(s.productivity_percent)}</div></div>
        <div className="text-center"><div className="text-[11px] font-semibold uppercase text-[var(--text-secondary)] tracking-wider">{t('idleTime.idleCost')}</div><div className="text-lg font-bold text-content">{fEur(s.idle_cost_eur)}</div></div>
        <div className="text-center"><div className="text-[11px] font-semibold uppercase text-[var(--text-secondary)] tracking-wider">{t('idleTime.totalIdleH')}</div><div className="text-lg font-bold text-content">{fH(s.total_idle_hours)}</div></div>
      </div>

      {/* Top 15 Leaderboard */}
      {lb.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line">
              <th className="px-3 py-2 text-[11px] font-semibold uppercase text-[var(--text-secondary)] text-center w-12">#</th>
              <th className="px-3 py-2 text-[11px] font-semibold uppercase text-[var(--text-secondary)] text-left">{t('idleTime.employee')}</th>
              <th className="px-3 py-2 text-[11px] font-semibold uppercase text-[var(--text-secondary)] text-right">{t('idleTime.avgGap')}</th>
              <th className="px-3 py-2 text-[11px] font-semibold uppercase text-[var(--text-secondary)] text-right">{t('idleTime.productivityPct')}</th>
              <th className="px-3 py-2 text-[11px] font-semibold uppercase text-[var(--text-secondary)] text-right">{t('idleTime.idleCostEur')}</th>
              <th className="px-3 py-2 text-[11px] font-semibold uppercase text-[var(--text-secondary)] text-right">{t('idleTime.score')}</th>
            </tr>
          </thead>
          <tbody>
            {lb.map(row => (
              <tr key={row.employee_name} className="border-b border-line-subtle hover:bg-surface-hover">
                <td className="px-3 py-2 text-center text-[var(--text-secondary)]">{row.rank}</td>
                <td className="px-3 py-2 text-left text-content font-medium">{row.employee_name}</td>
                <td className="px-3 py-2 text-right font-mono text-content">{fMin(row.avg_idle_minutes)}</td>
                <td className="px-3 py-2 text-right font-mono text-content">{fPct(row.productivity_percent)}</td>
                <td className="px-3 py-2 text-right font-mono text-content">{fEur(row.idle_cost_eur)}</td>
                <td className="px-3 py-2 text-right">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[var(--text-primary)]">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${row.score >= 75 ? 'bg-emerald-500' : row.score >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} />
                    {row.score}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Link to full report */}
      <div className="mt-4 text-center">
        <Link to="/idle-time" className="text-sm text-primary-500 hover:text-primary-400 font-medium">
          {t('idleTime.fullReport')} →
        </Link>
      </div>
    </div>
  );
}
